import React from "react";
import { Grid, Typography, Divider, Paper, Box, Stack, CircularProgress } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PlaceIcon from '@mui/icons-material/Place';
import SpeedIcon from '@mui/icons-material/Speed';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import { formatDateWithTime } from '@/shared';
import { usegetsolicitudcorporativa } from '@/shared/actions/erp/solicitudpreventacoorporativo/solicitur-preventa-corporativo.actions';
import { getEstadoConfig, getComplejidadConfig, chipStyle } from './estado-solicitud';

export interface ModalComentariosSolicitudProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  solicitud?: string;
}

const InfoItem: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const ModalComentariosSolicitud: React.FC<ModalComentariosSolicitudProps> = ({
  open,
  setOpen,
  solicitud
}) => {
  const handleClose = () => setOpen(false);

  const { data: response, isLoading } = solicitud
    ? usegetsolicitudcorporativa(solicitud)
    : { data: null, isLoading: false };

  const solicitudData = response?.data;
  const clientes = solicitudData?.clientes || [];

  const estadoCfg = getEstadoConfig(solicitudData?.estado_solicitud);
  const complejidadCfg = getComplejidadConfig(solicitudData?.complejidad);

  return (
    <ScrollableDialogProps
      open={open}
      title="Detalle de Solicitud Corporativa"
      cancelTextBtn="Cerrar"
      showConfirmBtn={false}
      onClose={handleClose}
      minWidth="55%"
      contentNode={
        isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} key="modal-content">

            {/* CABECERA */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: 'linear-gradient(180deg,#f8f9ff 0%,#ffffff 100%)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={2}
                >
                  <Box>
                    <Typography variant="overline" color="text.secondary">ID Único</Typography>
                    <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                      {solicitudData?.codigo ?? '—'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                    <Box textAlign="center">
                      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>Estado</Typography>
                      <span style={chipStyle(estadoCfg)}>{estadoCfg.label.toUpperCase()}</span>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>Complejidad</Typography>
                      {complejidadCfg
                        ? <span style={chipStyle(complejidadCfg)}>{complejidadCfg.label.toUpperCase()}</span>
                        : <Typography variant="body2" color="text.secondary">Sin asignar</Typography>}
                    </Box>
                  </Stack>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <InfoItem label="Usuario solicitante" value={solicitudData?.user_name} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoItem label="Usuario gestionó" value={solicitudData?.user_gestiona_name || 'Sin gestionar'} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoItem
                      label="Fecha de registro"
                      value={solicitudData?.fecha_creacion ? formatDateWithTime(solicitudData.fecha_creacion) : '—'}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoItem
                      label="Fecha de gestión"
                      value={solicitudData?.fecha_gestion ? formatDateWithTime(solicitudData.fecha_gestion) : '—'}
                    />
                  </Grid>
                </Grid>

                <Box mt={2}>
                  <InfoItem
                    label="Comentario general (gestor)"
                    value={solicitudData?.comentario_general?.trim() || 'Sin comentario'}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* CLIENTES Y SUCURSALES */}
            {clientes.map((cliente: any, index: number) => (
              <Grid item xs={12} key={cliente.id_solicitud_cliente ?? `cliente-${index}`}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <SettingsEthernetIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    Cliente {index + 1}: {cliente.cliente_oficina ?? 'N/A'}
                  </Typography>
                </Stack>

                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={6} md={3}><InfoItem label="Ciudad" value={cliente.ciudad} /></Grid>
                  <Grid item xs={6} md={3}><InfoItem label="Teléfono" value={cliente.telefono} /></Grid>
                  <Grid item xs={6} md={3}><InfoItem label="Dirección" value={cliente.direccion} /></Grid>
                  <Grid item xs={6} md={3}><InfoItem label="Contacto" value={cliente.contacto_sitio} /></Grid>
                </Grid>

                <Grid container spacing={2}>
                  {(cliente.sucursales || []).map((suc: any, i: number) => (
                    <Grid item xs={12} md={6} key={suc.id_solicitud_sucursal ?? `suc-${i}`}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%', borderLeft: '4px solid #5e5bdc' }}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                          <StorefrontIcon fontSize="small" color="primary" />
                          <Typography variant="subtitle2" fontWeight="bold" color="primary">
                            {suc.nombre_sucursal}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SpeedIcon fontSize="small" sx={{ color: '#777' }} />
                          <Typography variant="body2"><b>Velocidad:</b> {suc.velocidad} Kbps</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ ml: 0.5 }}><b>Servicio:</b> {suc.servicio ?? 'N/A'}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PlaceIcon fontSize="small" sx={{ color: '#777' }} />
                          <Typography variant="body2"><b>Coordenadas:</b> {suc.coordenadas ?? 'N/A'}</Typography>
                        </Stack>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary">Comentario</Typography>
                        <Typography variant="body2">
                          {suc.comentario?.trim() || 'Sin comentarios en esta sucursal'}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {(!cliente.sucursales || cliente.sucursales.length === 0) && (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                    Este cliente no cuenta con sucursales asociadas.
                  </Typography>
                )}
              </Grid>
            ))}

            {clientes.length === 0 && (
              <Grid item xs={12} sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  Esta solicitud no cuenta con clientes asociados.
                </Typography>
              </Grid>
            )}
          </Grid>
        )
      }
    />
  );
};

export default ModalComentariosSolicitud;
